import type { Orientation } from '../../../shared/games/battleship/index.js';

/**
 * Top-down 2D ship silhouettes (suunnitelma.md sections 10 and 30). Each ship type has its own
 * artwork, drawn in a horizontal coordinate system (bow to the right, 100 units per cell).
 * The vertical version is the same artwork turned bow-up.
 */

const U = 100; // units per cell

/** Hull outline with a pointed bow (right) and a rounded stern (left). */
function hull(length: number, beam: number): string {
  const top = 50 - beam / 2;
  const bottom = 50 + beam / 2;
  const stern = 10;
  const bowStart = length - Math.min(length * 0.28, 110);
  const tip = length - 6;
  return [
    `M ${stern + 14} ${top}`,
    `L ${bowStart} ${top}`,
    `C ${bowStart + (tip - bowStart) * 0.6} ${top} ${tip - 8} ${50 - beam * 0.18} ${tip} 50`,
    `C ${tip - 8} ${50 + beam * 0.18} ${bowStart + (tip - bowStart) * 0.6} ${bottom} ${bowStart} ${bottom}`,
    `L ${stern + 14} ${bottom}`,
    `C ${stern + 2} ${bottom} ${stern} ${bottom - 8} ${stern} 50`,
    `C ${stern} ${top + 8} ${stern + 2} ${top} ${stern + 14} ${top}`,
    'Z',
  ].join(' ');
}

/** Gun turret seen from above; the barrels point towards the bow (dir 1) or stern (dir -1). */
function Turret(props: { x: number; r: number; dir: 1 | -1; barrels?: number }) {
  const { x, r, dir } = props;
  const barrels = props.barrels ?? 2;
  const len = r * 1.9;
  const gap = r * 0.45;
  return (
    <g class="ship-detail">
      {Array.from({ length: barrels }, (_, i) => {
        const y = 50 + (i - (barrels - 1) / 2) * gap;
        return <rect x={dir === 1 ? x : x - len} y={y - 2.2} width={len} height={4.4} rx={2} />;
      })}
      <circle cx={x} cy={50} r={r} />
    </g>
  );
}

function Battleship() {
  const L = 4 * U;
  return (
    <>
      <path class="ship-body" d={hull(L, 62)} />
      <path class="ship-deck" d={hull(L, 44)} transform="translate(8 0)" />
      <Turret x={70} r={12} dir={-1} barrels={3} />
      <Turret x={110} r={11} dir={-1} />
      <rect class="ship-superstructure" x={150} y={36} width={95} height={28} rx={6} />
      <rect class="ship-detail" x={175} y={42} width={22} height={16} rx={4} />
      <circle class="ship-detail" cx={228} cy={50} r={6} />
      <Turret x={285} r={11} dir={1} />
      <Turret x={328} r={12} dir={1} barrels={3} />
    </>
  );
}

function Cruiser() {
  const L = 3 * U;
  return (
    <>
      <path class="ship-body" d={hull(L, 56)} />
      <path class="ship-deck" d={hull(L, 38)} transform="translate(6 0)" />
      <Turret x={62} r={10} dir={-1} />
      <rect class="ship-superstructure" x={100} y={38} width={80} height={24} rx={6} />
      <rect class="ship-detail" x={150} y={43} width={18} height={14} rx={3} />
      <circle class="ship-detail" cx={118} cy={50} r={5} />
      <Turret x={218} r={10} dir={1} />
    </>
  );
}

function Destroyer() {
  const L = 2 * U;
  return (
    <>
      <path class="ship-body" d={hull(L, 46)} />
      <path class="ship-deck" d={hull(L, 30)} transform="translate(5 0)" />
      <rect class="ship-superstructure" x={48} y={40} width={58} height={20} rx={5} />
      <circle class="ship-detail" cx={62} cy={50} r={5} />
      <Turret x={140} r={8} dir={1} barrels={1} />
    </>
  );
}

function PatrolBoat() {
  return (
    <>
      <path class="ship-body" d="M 18 34 L 58 32 C 76 34 88 42 94 50 C 88 58 76 66 58 68 L 18 66 C 10 66 8 60 8 50 C 8 40 10 34 18 34 Z" />
      <rect class="ship-superstructure" x={30} y={40} width={26} height={20} rx={5} />
      <rect class="ship-detail" x={45} y={44} width={8} height={12} rx={2} />
      <circle class="ship-detail" cx={74} cy={50} r={3.5} />
    </>
  );
}

function Artwork(props: { size: number }) {
  switch (props.size) {
    case 4:
      return <Battleship />;
    case 3:
      return <Cruiser />;
    case 2:
      return <Destroyer />;
    default:
      return <PatrolBoat />;
  }
}

export function ShipSprite(props: { size: number; orientation: Orientation; class?: string }) {
  const L = props.size * U;
  const vertical = props.orientation === 'v' && props.size > 1;
  return (
    <svg
      class={`ship ship-size-${props.size} ${props.class ?? ''}`}
      viewBox={vertical ? `0 0 ${U} ${L}` : `0 0 ${L} ${U}`}
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      {vertical ? (
        // Bow up: (x, y) → (y, L − x)
        <g transform={`translate(0 ${L}) rotate(-90)`}>
          <Artwork size={props.size} />
        </g>
      ) : (
        <Artwork size={props.size} />
      )}
    </svg>
  );
}
